export interface CountryOption {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

// Common countries with their dial codes and flags
export const COUNTRY_DATA: CountryOption[] = [
  { code: 'BT', name: 'Bhutan', dialCode: '+975', flag: '🇧🇹' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
];

// Default country (Bhutan)
export const DEFAULT_COUNTRY = COUNTRY_DATA[0];

/**
 * Find a country by its dial code
 */
export const findCountryByDialCode = (dialCode: string): CountryOption | undefined => {
  return COUNTRY_DATA.find((country) => country.dialCode === dialCode);
};

/**
 * Find a country by its country code
 */
export const findCountryByCode = (code: string): CountryOption | undefined => {
  return COUNTRY_DATA.find((country) => country.code === code);
};

/**
 * Parse a phone number to extract country code and local number
 */
export const parsePhoneNumber = (
  phoneNumber: string
): { country: CountryOption; localNumber: string } => {
  if (!phoneNumber) {
    return { country: DEFAULT_COUNTRY, localNumber: '' };
  }

  const trimmedPhone = phoneNumber.trim();

  if (trimmedPhone.startsWith('+')) {
    // Try to match the longest dial code first
    const sortedCountries = [...COUNTRY_DATA].sort((a, b) => b.dialCode.length - a.dialCode.length);

    for (const country of sortedCountries) {
      if (trimmedPhone.startsWith(country.dialCode)) {
        const localNumber = trimmedPhone.slice(country.dialCode.length).trim();

        return { country, localNumber };
      }
    }
  }

  return { country: DEFAULT_COUNTRY, localNumber: trimmedPhone.replace(/^\+/, '') };
};

/**
 * Format a phone number with country code
 * Returns dial code even if local number is empty (to preserve country selection)
 */
export const formatPhoneWithCountry = (country: CountryOption, localNumber: string): string => {
  const cleanNumber = localNumber
    .split('')
    .filter((char) => !/\s/.test(char))
    .join('');

  // Always include dial code to preserve country selection
  return cleanNumber ? `${country.dialCode}${cleanNumber}` : country.dialCode;
};

/**
 * Sanitize local phone number input - removes non-digit and non-space characters
 */
export const sanitizeLocalNumber = (input: string): string => {
  return input
    .split('')
    .filter((char) => /[\d\s]/.test(char))
    .join('');
};

/**
 * Convert country data to select options format
 * Label shows: flag + dial code + country name (for dropdown display)
 * Value is the country code
 */
export const getCountrySelectOptions = () => {
  return COUNTRY_DATA.map((country) => ({
    label: `${country.flag} ${country.dialCode} ${country.name}`,
    value: country.code,
  }));
};

/**
 * Get country name options for generic country selection.
 */
export const getCountryNameOptions = (): Array<{ label: string; value: string }> => {
  return COUNTRY_DATA.map((country) => ({
    label: country.name,
    value: country.code,
  })).sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Get display text for selected country (flag + dial code only)
 */
export const getCountryDisplayText = (countryCode: string): string => {
  const country = findCountryByCode(countryCode);

  if (country) {
    return `${country.flag} ${country.dialCode}`;
  }

  return '';
};
